import template from "./index.html";
import "./index.less";
import { FORM_REPORTS_BATCH } from "routes.js";
import { REPORT_SCOPE_MODES } from "../../components/report-scope/report-scope.js";
import {
	ACTION_COLLAPSE_DETAILS,
	ACTION_COLLAPSE_INVOICES,
	ACTION_EXPAND_DETAILS,
	ACTION_EXPAND_INVOICES
} from "constants.js";

const SORT_DIRECTIONS = {
	asc: "asc",
	desc: "desc"
};

const PAGE_SIZES = ["5", "10", "15", "25"];

function controller(
	$state,
	$location,
	$filter,
	$logger,
	$timeout,
	api,
	appState,
	loadingService,
	reportScopeFactory,
	toastr
) {
	const ctrl = this;

	ctrl.$onInit = onInit;
	ctrl.loading = false;
	ctrl.loaded = false;
	ctrl.allReports = [];
	ctrl.reportItems = [];
	ctrl.missingReportIds = [];
	ctrl.batchSummary = buildReportSummary([]);
	ctrl.pageSummary = buildReportSummary([]);
	ctrl.pageSizes = PAGE_SIZES;
	ctrl.paginatorOptions = {
		currentPage: 1,
		itemsPerPage: PAGE_SIZES[0],
		totalItems: 0
	};
	ctrl.updateSort = updateSort;
	ctrl.updateReportPage = updateReportPage;
	ctrl.toggleSortDirection = toggleSortDirection;
	ctrl.getFirstVisibleReportNumber = getFirstVisibleReportNumber;
	ctrl.getLastVisibleReportNumber = getLastVisibleReportNumber;
	ctrl.canPrint = canPrint;
	ctrl.print = print;

	const buildViewReportsEvent = $logger.buildWebEvent("viewReports");

	function onInit() {
		ctrl.form = appState.forms.selected;
		ctrl.reportIds = parseReportIds($state.params.ids);
		ctrl.sortOptions = buildSortOptions();
		ctrl.sortOption = getSortOption($state.params.sort);
		ctrl.sortDirection = getSortDirection($state.params.direction);

		if(ctrl.reportIds.length === 0) {
			$state.go("authenticated.report-list", {}, { location: "replace" });
			return;
		}

		loadReports();
	}

	async function loadReports() {
		ctrl.loading = true;
		ctrl.error = undefined;
		ctrl.warning = undefined;

		try {
			$logger.event(buildViewReportsEvent("load")("requested"), {
				formId: ctrl.form.Id,
				reportIds: ctrl.reportIds
			});

			const reports = await loadingService.watch(
				api.post(FORM_REPORTS_BATCH(ctrl.form.Id), ctrl.reportIds),
				`Loading ${pluralReport(ctrl.reportIds.length, true)}...`
			);

			const reportById = (reports ?? []).reduce((acc, report) => {
				acc[report.Id] = report;
				return acc;
			}, {});

			ctrl.allReports = ctrl.reportIds
				.map(id => reportById[id])
				.filter(report => !!report && report.FormId === ctrl.form.Id && report.IsNegative !== true);

			sortReportItems();
			ctrl.paginatorOptions.totalItems = ctrl.allReports.length;
			ctrl.batchSummary = buildReportSummary(ctrl.allReports);
			updateReportPage();

			const renderedReportIds = ctrl.allReports.map(report => report.Id);
			ctrl.missingReportIds = ctrl.reportIds.filter(id => renderedReportIds.indexOf(id) === -1);

			if(ctrl.missingReportIds.length > 0) {
				ctrl.warning = `${ctrl.missingReportIds.length} selected ${pluralReport(ctrl.missingReportIds.length).toLowerCase()} could not be displayed.`;
			}

			if(ctrl.allReports.length === 0) {
				ctrl.error = `No ${pluralReport(2).toLowerCase()} could be displayed.`;
			}

			$logger.event(buildViewReportsEvent("load")("succeeded"), {
				formId: ctrl.form.Id,
				reportIds: renderedReportIds,
				missingReportIds: ctrl.missingReportIds
			});
		}
		catch(err) {
			$logger.exception(err, "Failure occurred while loading reports for view.");
			$logger.event(buildViewReportsEvent("load")("failed"), {
				formId: ctrl.form.Id,
				reportIds: ctrl.reportIds
			});

			ctrl.error = `The selected ${pluralReport(2).toLowerCase()} could not be loaded.`;
			toastr.error(ctrl.error);
		}
		finally {
			ctrl.loading = false;
			ctrl.loaded = true;
		}
	}

	function buildSortOptions() {
		return [
			{
				key: "number",
				display: `${ctrl.form.nomenclature.PositiveReport} #`,
				property: "Number",
				type: "number"
			},
			{
				key: "created",
				display: "Created",
				property: "Created",
				type: "date"
			},
			{
				key: "lastModified",
				display: "Last Modified",
				property: "LastModified",
				type: "date"
			}
		];
	}

	function getSortOption(key) {
		return ctrl.sortOptions.find(option => option.key === key) || ctrl.sortOptions[0];
	}

	function getSortDirection(direction) {
		return direction === SORT_DIRECTIONS.desc ? SORT_DIRECTIONS.desc : SORT_DIRECTIONS.asc;
	}

	function updateSort() {
		sortReportItems();
		ctrl.batchSummary = buildReportSummary(ctrl.allReports);
		updateReportPage();
		updateSortRoute();
	}

	function toggleSortDirection() {
		ctrl.sortDirection = ctrl.sortDirection === SORT_DIRECTIONS.asc
			? SORT_DIRECTIONS.desc
			: SORT_DIRECTIONS.asc;

		updateSort();
	}

	function sortReportItems() {
		ctrl.allReports = ctrl.allReports.slice().sort(compareReports);
	}

	function compareReports(left, right) {
		const sortResult = compareValues(
			getSortValue(left),
			getSortValue(right)
		);

		if(sortResult !== 0) {
			return sortResult;
		}

		return compareValues(left.Id, right.Id);
	}

	function getSortValue(report) {
		const rawValue = report[ctrl.sortOption.property];

		if(ctrl.sortOption.type === "date") {
			const timestamp = Date.parse(rawValue);
			return isNaN(timestamp) ? null : timestamp;
		}

		if(ctrl.sortOption.type === "number") {
			const number = Number(rawValue);
			return isNaN(number) ? null : number;
		}

		return rawValue;
	}

	function compareValues(leftValue, rightValue) {
		const leftEmpty = leftValue === null || leftValue === undefined || leftValue === "";
		const rightEmpty = rightValue === null || rightValue === undefined || rightValue === "";

		if(leftEmpty && rightEmpty) {
			return 0;
		}

		if(leftEmpty) {
			return 1;
		}

		if(rightEmpty) {
			return -1;
		}

		if(leftValue === rightValue) {
			return 0;
		}

		const direction = ctrl.sortDirection === SORT_DIRECTIONS.asc ? 1 : -1;
		return leftValue > rightValue ? direction : -direction;
	}

	function updateSortRoute() {
		$location
			.search("sort", ctrl.sortOption.key)
			.search("direction", ctrl.sortDirection)
			.replace();
	}

	function updateReportPage() {
		const currentPage = Number(ctrl.paginatorOptions.currentPage);
		const itemsPerPage = Number(ctrl.paginatorOptions.itemsPerPage);
		const startIndex = (currentPage - 1) * itemsPerPage;
		const pageReports = ctrl.allReports.slice(startIndex, startIndex + itemsPerPage);

		ctrl.pageSummary = buildReportSummary(pageReports);
		ctrl.reportItems = pageReports.map(report => ({
			report,
			reportScope: reportScopeFactory.create({
				form: ctrl.form,
				report,
				mode: REPORT_SCOPE_MODES.minimal,
				isIsolated: false,
				isReadOnly: true
			})
		}));
	}

	function buildReportSummary(reports) {
		return {
			count: reports.length,
			reportNumberRange: buildReportNumberRange(reports),
			createdRange: buildDateRange(reports, "Created"),
			lastModifiedRange: buildDateRange(reports, "LastModified")
		};
	}

	function buildReportNumberRange(reports) {
		const reportNumbers = reports
			.map(report => report.Number)
			.filter(hasValue);

		if(reportNumbers.length === 0) {
			return "--";
		}

		const numericReportNumbers = reportNumbers
			.map(reportNumber => ({
				raw: reportNumber,
				value: Number(reportNumber)
			}))
			.filter(reportNumber => !isNaN(reportNumber.value));

		if(numericReportNumbers.length === reportNumbers.length) {
			const sortedNumericReportNumbers = numericReportNumbers.sort((left, right) => left.value - right.value);
			const firstReportNumber = sortedNumericReportNumbers[0].raw;
			const lastReportNumber = sortedNumericReportNumbers[sortedNumericReportNumbers.length - 1].raw;
			return buildRangeLabel(firstReportNumber, lastReportNumber);
		}

		const sortedReportNumbers = reportNumbers
			.map(reportNumber => `${reportNumber}`)
			.sort();

		return buildRangeLabel(sortedReportNumbers[0], sortedReportNumbers[sortedReportNumbers.length - 1]);
	}

	function buildDateRange(reports, property) {
		const timestamps = reports
			.map(report => toTimestamp(report[property]))
			.filter(hasValue)
			.sort((left, right) => left - right);

		if(timestamps.length === 0) {
			return "--";
		}

		return buildRangeLabel(
			formatDate(timestamps[0]),
			formatDate(timestamps[timestamps.length - 1])
		);
	}

	function buildRangeLabel(firstValue, lastValue) {
		return firstValue === lastValue
			? `${firstValue}`
			: `${firstValue} - ${lastValue}`;
	}

	function formatDate(timestamp) {
		return $filter("date")(timestamp, "MM/dd/yyyy hh:mm a");
	}

	function toTimestamp(value) {
		if(!hasValue(value)) {
			return null;
		}

		if(typeof value.valueOf === "function") {
			const timestamp = Number(value.valueOf());
			if(!isNaN(timestamp)) {
				return timestamp;
			}
		}

		const timestamp = Date.parse(value);
		return isNaN(timestamp) ? null : timestamp;
	}

	function hasValue(value) {
		return value !== null && value !== undefined && value !== "";
	}

	function getFirstVisibleReportNumber() {
		if(ctrl.paginatorOptions.totalItems === 0) {
			return 0;
		}

		return ((Number(ctrl.paginatorOptions.currentPage) - 1) * Number(ctrl.paginatorOptions.itemsPerPage)) + 1;
	}

	function getLastVisibleReportNumber() {
		const lastVisibleReportNumber = Number(ctrl.paginatorOptions.currentPage) * Number(ctrl.paginatorOptions.itemsPerPage);

		return Math.min(lastVisibleReportNumber, ctrl.paginatorOptions.totalItems);
	}

	function canPrint() {
		return ctrl.loaded && !ctrl.loading && !ctrl.error && ctrl.reportItems.length > 0;
	}

	function print() {
		if(!canPrint()) {
			return;
		}

		$logger.event(buildViewReportsEvent("print")("requested"), {
			formId: ctrl.form.Id,
			reportIds: ctrl.reportItems.map(item => item.report.Id)
		});

		triggerPrintEvent(ACTION_EXPAND_DETAILS);
		triggerPrintEvent(ACTION_EXPAND_INVOICES);

		let didCollapse = false;
		let fallbackTimer = null;

		const collapseAfterPrint = () => {
			if(didCollapse) {
				return;
			}

			didCollapse = true;
			if(fallbackTimer !== null) {
				clearTimeout(fallbackTimer);
			}

			triggerPrintEvent(ACTION_COLLAPSE_DETAILS);
			triggerPrintEvent(ACTION_COLLAPSE_INVOICES);
		};

		const printMedia = window.matchMedia ? window.matchMedia("print") : null;

		if(printMedia && printMedia.addEventListener) {
			const mediaListener = (event) => {
				if(event.matches === false) {
					collapseAfterPrint();
					printMedia.removeEventListener("change", mediaListener);
				}
			};

			printMedia.addEventListener("change", mediaListener);
		}

		window.addEventListener("afterprint", collapseAfterPrint, { once: true });

		$timeout(() => {
			window.print();
			$logger.event(buildViewReportsEvent("print")("executed"), {
				formId: ctrl.form.Id,
				reportIds: ctrl.reportItems.map(item => item.report.Id)
			});

			fallbackTimer = setTimeout(() => {
				collapseAfterPrint();
			}, 3000);
		});
	}

	function triggerPrintEvent(eventName) {
		ctrl.reportItems.forEach(item => item.reportScope.events.trigger(eventName));
	}

	function parseReportIds(ids) {
		const rawIds = Array.isArray(ids) ? ids : `${ids || ""}`.split(",");

		return rawIds
			.map(id => parseInt(id, 10))
			.filter((id, index, allIds) => Number.isInteger(id) && id > 0 && allIds.indexOf(id) === index);
	}

	function pluralReport(count, includeCount) {
		return $filter("pluralize")(ctrl.form.nomenclature.PositiveReport, count, includeCount);
	}

}

export default angular
	.module("vesl.screens.view-reports", [])
	.component("viewReports", {
		template,
		controller
	}).name;
